
import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { FiUploadCloud, FiArrowRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import Button from '../Button';
import Select from '../Select';
import Api from '../../Api';
import './style.css';

const DB_FIELDS = [
  { value: 'name', label: 'Nome' },
  { value: 'type', label: 'Tipo (Receita/Despesa)' },
  { value: 'parent_name', label: 'Categoria Pai (Nome)' },
  { value: 'description', label: 'Descrição' },
  { value: 'active', label: 'Ativo (Sim/Não)' },
];

export default function CategoryImportModal({ isOpen, onClose, onSuccess, companyId, companies = [] }) {
  const [step, setStep] = useState('upload'); // upload, map, review, processing
  const [selectedCompanyId, setSelectedCompanyId] = useState(companyId || '');
  const [fileData, setFileData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({}); // { headerName: dbField }
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    setSelectedCompanyId(companyId || '');
  }, [companyId]);

  const handleFileChange = (e) => {
    if (!selectedCompanyId) {
      toast.warning('Selecione uma empresa antes de carregar o arquivo.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 }); // Array of arrays

        if (data.length < 2) {
          toast.warning('O arquivo parece estar vazio ou sem dados.');
          return;
        }

        const fileHeaders = data[0];
        const rows = data.slice(1);

        setHeaders(fileHeaders);
        setFileData(rows);
        
        // Auto-map based on similar names
        const initialMapping = {};
        fileHeaders.forEach(header => {
          const lowerHeader = header.toLowerCase();
          if (lowerHeader.includes('nome')) initialMapping[header] = 'name';
          else if (lowerHeader.includes('pai') || lowerHeader.includes('superior') || lowerHeader.includes('parent')) initialMapping[header] = 'parent_name';
          else if (lowerHeader.includes('tipo')) initialMapping[header] = 'type';
          else if (lowerHeader.includes('descri')) initialMapping[header] = 'description';
          else if (lowerHeader.includes('ativo') || lowerHeader.includes('status')) initialMapping[header] = 'active';
        });
        
        setMapping(initialMapping);
        setStep('map');
      } catch (error) {
        console.error(error);
        toast.error('Erro ao ler o arquivo.');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleMappingChange = (header, dbField) => {
    setMapping(prev => ({ ...prev, [header]: dbField }));
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      
      // Transform data based on mapping
      const itemsToCreate = fileData.map(row => {
        const item = { company_id: selectedCompanyId };
        
        headers.forEach((header, index) => {
          const dbField = mapping[header];
          if (dbField) {
            let value = row[index];
            
            // Data cleaning/formatting
            if (dbField === 'active') {
                if (typeof value === 'string') {
                    const lower = value.toLowerCase();
                    value = lower === 'sim' || lower === 's' || lower === 'true' || lower === 'ativo';
                } else {
                    value = !!value;
                }
            }
            if (dbField === 'type') {
                 // Try to normalize Receita/Despesa
                 if (typeof value === 'string') {
                    const lower = value.toLowerCase();
                    if (lower.includes('receita') || lower === 'r') value = 'Receita';
                    else if (lower.includes('despesa') || lower === 'd') value = 'Despesa';
                    else value = 'Despesa'; // Default
                 } else {
                     value = 'Despesa';
                 }
            }

            item[dbField] = value;
          }
        });

        // Defaults
        if (item.active === undefined) item.active = true;
        if (!item.type) item.type = 'Despesa';
        
        return item;
      }).filter(s => s.name); // Ensure name exists

      if (itemsToCreate.length === 0) {
        toast.warning('Nenhum dado válido para importar.');
        setLoading(false);
        return;
      }

      const result = await Api.importCategories(selectedCompanyId, { categories: itemsToCreate });
      
      toast.success(`${result.createdCount} categorias importadas com sucesso!`);
      if (result.errorsCount > 0) {
          toast.warning(`${result.errorsCount} erros ocorreram. Verifique o console.`);
          console.warn('Erros de importação:', result.errors);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao importar dados. Verifique o console.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setFileData([]);
    setHeaders([]);
    setMapping({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { reset(); onClose(); }}
      title="Importar Categorias"
      size="lg"
    >
      <div className="import-modal-body">
        <div className="import-content-area">
            {step === 'upload' && (
                <>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <Select
                      label="Empresa"
                      value={selectedCompanyId}
                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                      options={[
                        { value: '', label: 'SELECIONE UMA EMPRESA...' },
                        ...companies.map(c => ({
                          value: c.id,
                          label: c.trade_name || c.corporate_name || c.name
                        }))
                      ]}
                      searchable
                      placeholder="SELECIONE UMA EMPRESA..."
                    />
                  </div>
                  <div 
                      className="upload-area"
                      onClick={() => fileInputRef.current.click()}
                  >
                      <FiUploadCloud className="upload-icon" />
                      <p className="upload-text">Clique para selecionar um arquivo Excel (.xlsx) ou CSV</p>
                      <p className="upload-subtext">O arquivo deve conter cabeçalhos na primeira linha.</p>
                      <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept=".xlsx, .xls, .csv"
                          style={{ display: 'none' }}
                      />
                  </div>
                </>
            )}

            {step === 'map' && (
                <div className="map-step">
                    <p className="step-description">Mapeie as colunas do seu arquivo para os campos do sistema.</p>
                    
                    <div className="mapping-grid">
                        {headers.map((header, idx) => (
                            <div key={idx} className="mapping-row">
                                <div className="file-header">
                                    <span className="label">Coluna do Arquivo:</span>
                                    <strong>{header}</strong>
                                    {fileData[0] && (
                                        <span className="example-value">Ex: {fileData[0][idx]}</span>
                                    )}
                                </div>
                                <div className="arrow-icon">
                                    <FiArrowRight />
                                </div>
                                <div className="db-field-select">
                                    <Select
                                        options={[{ value: '', label: 'Ignorar' }, ...DB_FIELDS]}
                                        value={mapping[header] || ''}
                                        onChange={(e) => handleMappingChange(header, e.target.value)}
                                        placeholder="Selecione o campo..."
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>

        <div className="modal-actions">
            <Button 
                onClick={() => step === 'upload' ? onClose() : reset()} 
                className="cancel-button"
                disabled={loading}
            >
                {step === 'upload' ? 'Cancelar' : 'Voltar'}
            </Button>
            
            {step === 'map' && (
                <Button onClick={handleImport} disabled={loading}>
                    {loading ? 'Importando...' : 'Importar Dados'}
                </Button>
            )}
        </div>
      </div>
    </Modal>
  );
}
